<template>
  <div
    :data-cy="shortcut.key"
    class="group flex items-center justify-between gap-3 bg-white px-4 py-2.5 transition-colors hover:bg-grey-hover"
    :class="{ 'border-b border-divider': !isLast }"
  >
    <span
      class="min-w-0 flex-1 truncate text-sm text-headline"
      :title="actionLabel"
    >
      {{ actionLabel }}
    </span>
    <div class="flex shrink-0 items-center gap-2">
      <shortcut-key-badges :shortcut="shortcut" />
      <div
        v-if="editable"
        class="flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100"
        :class="{ 'opacity-100': popoverOpen }"
      >
        <button
          v-if="isCustom"
          :data-cy="`restore-${shortcut.key}`"
          class="flex size-6 items-center justify-center text-label hover:text-headline"
          @click="$emit('reset', shortcut)"
        >
          <icon-base
            :icon="IconRestore"
            width="16"
            height="16"
            view-box="0 0 15 14"
          />
        </button>
        <reassign-shortcut-popover
          ref="reassignRef"
          :shortcut="shortcut"
          @save="$emit('save', $event)"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, useTemplateRef } from "vue";

import startCase from "lodash/startCase";

import IconRestore from "@/components/icons/shortcuts/IconRestore.vue";
import ReassignShortcutPopover from "@/components/shortcuts/ReassignShortcutPopover.vue";
import ShortcutKeyBadges from "@/components/shortcuts/ShortcutKeyBadges.vue";
import IconBase from "@/components/ui/IconBase.vue";

import type {
  KeyboardShortcutUpdate,
  UserKeyboardShortcut,
} from "@/models/options";

const { shortcut, editable = false } = defineProps<{
  shortcut: UserKeyboardShortcut;
  editable?: boolean;
  isLast?: boolean;
}>();

defineEmits<{
  save: [KeyboardShortcutUpdate];
  reset: [UserKeyboardShortcut];
}>();

const reassignRef = useTemplateRef("reassignRef");

const popoverOpen = computed(() => reassignRef.value?.isOpen ?? false);
const isCustom = computed(
  () =>
    shortcut.is_custom ||
    !Object.prototype.hasOwnProperty.call(shortcut, "is_custom")
);

const actionLabel = computed(() => startCase(shortcut.action_name));
</script>
